import type { Phase4QaIdentityId } from './local-phase4-qa';

const QA_SESSION_PATTERN = /^[a-z0-9-]{1,40}$/;

export interface LocalQaUrlContext {
  session: string;
  viewer: Phase4QaIdentityId;
}

export function readLocalQaUrlContext(search: string): LocalQaUrlContext | null {
  const params = new URLSearchParams(search);
  if (params.get('coastersPlatform') !== 'local-qa') return null;
  const requestedSession = params.get('coastersQaSession') ?? 'default';
  const session = QA_SESSION_PATTERN.test(requestedSession) ? requestedSession : 'default';
  return {
    session,
    viewer: params.get('coastersQaUser') === 'qa.bob' ? 'qa.bob' : 'qa.alice',
  };
}

export function localQaStorageKey(session: string): string {
  return session === 'default'
    ? 'buncha-coasters.phase4-qa-platform.v1'
    : `buncha-coasters.phase4-qa-platform.v1.${session}`;
}

export function otherLocalQaViewer(viewer: Phase4QaIdentityId): Phase4QaIdentityId {
  return viewer === 'qa.alice' ? 'qa.bob' : 'qa.alice';
}

function addLocalQaVisitorContext(
  url: URL,
  context: LocalQaUrlContext | null,
  targetViewer?: Phase4QaIdentityId,
): void {
  if (!context) return;
  url.searchParams.set('coastersPlatform', 'local-qa');
  url.searchParams.set('coastersQaSession', context.session);
  url.searchParams.set('coastersQaUser', targetViewer ?? otherLocalQaViewer(context.viewer));
}

export function resolvePublicBaseUrl(options: {
  currentHref: string;
  configuredBaseUrl?: string;
  localQa: LocalQaUrlContext | null;
}): string {
  if (options.localQa)
    return new URL(options.configuredBaseUrl || new URL(options.currentHref).origin).toString();
  return new URL(options.configuredBaseUrl || new URL(options.currentHref).origin).origin;
}

function relativePublicPath(path: string): string {
  return path.replace(/^\/+/, '');
}

export function buildReadOnlyShareUrl(options: {
  sharePath: string;
  publicBaseUrl: string;
  localQa: LocalQaUrlContext | null;
  targetViewer?: Phase4QaIdentityId;
}): string {
  const url = new URL(relativePublicPath(options.sharePath), options.publicBaseUrl);
  addLocalQaVisitorContext(url, options.localQa, options.targetViewer);
  return url.toString();
}

export function buildPublicCoasterUrl(options: {
  publicSlug: string;
  publicBaseUrl: string;
  localQa: LocalQaUrlContext | null;
  targetViewer?: Phase4QaIdentityId;
}): string {
  const url = new URL(`coasters/${encodeURIComponent(options.publicSlug)}`, options.publicBaseUrl);
  addLocalQaVisitorContext(url, options.localQa, options.targetViewer);
  return url.toString();
}

export function buildEditorResetPath(options: {
  currentHref: string;
  basePath: string;
  localQa: LocalQaUrlContext | null;
}): string {
  const current = new URL(options.currentHref);
  const target = new URL(options.basePath || '/', current);
  if (options.localQa) {
    target.searchParams.set('coastersPlatform', 'local-qa');
    target.searchParams.set('coastersQaSession', options.localQa.session);
    target.searchParams.set('coastersQaUser', options.localQa.viewer);
  }
  return `${target.pathname}${target.search}`;
}
